import { Injectable } from '@angular/core';
import Fuse, { IFuseOptions } from 'fuse.js';
import { Product, ProductSearchResult } from '../models/product.model';

/**
 * SearchService com Fuse.js
 * Busca inteligente com correção de erros de digitação e fuzzy search
 */
@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private fuse: Fuse<Product> | null = null;

  // Configuração do Fuse.js para busca fuzzy
  private fuseOptions: IFuseOptions<Product> = {
    keys: [
      { name: 'nome', weight: 2 },           // Maior peso para nome
      { name: 'codigo', weight: 1.5 },       // Peso médio para código
      { name: 'descricaoCurta', weight: 1 },
      { name: 'descricao', weight: 0.5 },
      { name: 'categoria.nome', weight: 1.2 },
      { name: 'veiculoCompativel.marca', weight: 1.5 },
      { name: 'veiculoCompativel.modelo', weight: 1.5 },
    ],
    threshold: 0.4,           // 0.4 = permite ~40% de diferença (ótimo para typos)
    distance: 100,            // Distância máxima entre caracteres
    minMatchCharLength: 2,    // Mínimo 2 caracteres para match
    includeScore: true,       // Incluir score de relevância
    useExtendedSearch: true,  // Permite buscas mais complexas
    ignoreLocation: true,     // Não importa onde no texto está o match
  };

  constructor() { }

  /**
   * Inicializa o Fuse.js com os produtos
   */
  initializeSearch(products: Product[]): void {
    this.fuse = new Fuse(products, this.fuseOptions);
  }

  /**
   * Busca inteligente nos produtos
   * Exemplos:
   * - "faro" encontra "farol"
   * - "uno 2010" encontra produtos para Fiat Uno 2010
   * - "lanterna onix" encontra lanternas do Onix
   */
  intelligentSearch(query: string): ProductSearchResult[] {
    if (!this.fuse || !query || query.trim().length < 2) {
      return [];
    }

    const results = this.fuse.search(query.trim());
    
    return results.map(result => ({
      product: result.item,
      score: result.score
    }));
  }

  /**
   * Busca com sugestões (para autocomplete)
   * Retorna top 8 resultados
   */
  searchSuggestions(query: string, limit: number = 8): Product[] {
    const results = this.intelligentSearch(query);
    return results.slice(0, limit).map(r => r.product);
  }

  /**
   * Busca simples (sem Fuse.js) - fallback
   * Busca por substring nos campos principais
   */
  simpleSearch(products: Product[], query: string): Product[] {
    if (!query || query.trim().length < 2) {
      return products;
    }

    const searchTerm = query.toLowerCase().trim();
    
    return products.filter(p => {
      const searchableText = [
        p.nome,
        p.codigo,
        p.descricaoCurta || '',
        p.categoria.nome,
        ...p.veiculoCompativel.map(v => `${v.marca} ${v.modelo}`)
      ].join(' ').toLowerCase();

      return searchableText.includes(searchTerm);
    });
  }

  /**
   * Destaca (highlight) os termos encontrados em um texto
   * Útil para mostrar nas sugestões
   */
  highlightMatches(text: string, query: string): string {
    if (!query || query.trim().length < 2) {
      return text;
    }

    const searchTerms = query.trim().toLowerCase().split(' ');
    let result = text;

    searchTerms.forEach(term => {
      if (term.length >= 2) {
        const regex = new RegExp(`(${term})`, 'gi');
        result = result.replace(regex, '<mark>$1</mark>');
      }
    });

    return result;
  }

  /**
   * Extrai termos de busca relacionados a veículos
   * Ex: "farol uno 2012" -> { possibleMake: 'fiat', possibleModel: 'uno', possibleYear: 2012 }
   */
  extractVehicleInfo(query: string): { 
    possibleMake?: string; 
    possibleModel?: string; 
    possibleYear?: number;
  } {
    const result: any = {};
    
    // Busca por ano (4 dígitos entre 2000-2030)
    const yearMatch = query.match(/\b(20[0-2][0-9]|2030)\b/);
    if (yearMatch) {
      result.possibleYear = parseInt(yearMatch[0]);
    }

    // Marcas conhecidas
    const knownMakes = ['fiat', 'volkswagen', 'vw', 'chevrolet', 'gm', 'ford', 'honda', 'toyota', 'hyundai', 'renault', 'nissan', 'peugeot'];
    const lowerQuery = query.toLowerCase();
    
    for (const make of knownMakes) {
      if (lowerQuery.includes(make)) {
        result.possibleMake = make === 'vw' ? 'volkswagen' : make === 'gm' ? 'chevrolet' : make;
        break;
      }
    }

    // Modelos populares (simplificado)
    const knownModels = ['uno', 'palio', 'strada', 'gol', 'fox', 'polo', 'onix', 'prisma', 'corsa', 'ka', 'fiesta', 'civic', 'fit', 'corolla', 'hb20', 'creta'];
    
    for (const model of knownModels) {
      if (lowerQuery.includes(model)) {
        result.possibleModel = model;
        break;
      }
    }

    return result;
  }
}

